// Custom metric cards from the assistant (plan 2026-10-16 custom metrics
// through the MCP and the assistant, step 6).
//
// Each executor calls the same REST client the Data sources dialog and the
// Semantic Layer page use, under the user's own JWT: the backend's dashboard
// metric routes stay the gate. Update, delete and promote pass the card's
// `if_match`, so a metric that changed after the card was offered is refused
// (409) instead of overwritten. Every write drops the cached composition
// catalogs, so the block editor and the structure view see it at once.
import {
  createDashboardMetric,
  deleteDashboardMetric,
  updateDashboardMetric,
  type CreateDashboardMetricRequest,
  type UpdateDashboardMetricRequest,
} from "@/api/dashboards";
import { promoteCustomMetric } from "@/api/adminSemantics";
import { invalidateCompositionCatalog } from "@/components/dashboards/composition/useCompositionCatalog";
import { registerAction } from "./registry";

function dashboardLink(dashboardId: string) {
  return { label: "Open dashboard", to: `/insights/dashboards/${dashboardId}` };
}

const SEMANTIC_LAYER_LINK = { label: "Open Semantic Layer", to: "/data-management/semantic-layer" };

registerAction("create_dashboard_metric", async (params) => {
  const { dashboard_id: dashboardId, ...body } = params as { dashboard_id: string } & Record<string, unknown>;
  const metric = await createDashboardMetric(dashboardId, body as unknown as CreateDashboardMetricRequest);
  invalidateCompositionCatalog();
  return { resultRef: { metric_id: metric.id, key: metric.key }, link: dashboardLink(dashboardId) };
});

registerAction("update_dashboard_metric", async (params) => {
  const { dashboard_id: dashboardId, metric_id: metricId, ...body } = params as {
    dashboard_id: string;
    metric_id: string;
  } & Record<string, unknown>;
  const metric = await updateDashboardMetric(dashboardId, metricId, body as unknown as UpdateDashboardMetricRequest);
  invalidateCompositionCatalog();
  return { resultRef: { metric_id: metric.id, key: metric.key }, link: dashboardLink(dashboardId) };
});

registerAction("delete_dashboard_metric", async (params) => {
  const dashboardId = params.dashboard_id as string;
  const metricId = params.metric_id as string;
  await deleteDashboardMetric(dashboardId, metricId, params.if_match as string);
  invalidateCompositionCatalog();
  return { resultRef: { metric_id: metricId }, link: dashboardLink(dashboardId) };
});

registerAction("promote_custom_metric", async (params) => {
  const metricId = params.metric_id as string;
  const metric = await promoteCustomMetric(metricId, params.if_match as string);
  invalidateCompositionCatalog();
  return { resultRef: { metric_id: metric.id, key: metric.key }, link: SEMANTIC_LAYER_LINK };
});
